import { useEffect, useState } from "react";

export type PipelineObjectType = "deals" | "tickets";

export interface PipelineStage {
  id: string;
  label: string;
  displayOrder: number;
  metadata?: { probability?: string | number };
}

export interface Pipeline {
  id: string;
  label: string;
  displayOrder?: number;
  stages: PipelineStage[];
}

interface ApiResponse {
  results?: Pipeline[];
}

export function usePipelines(
  portalId: string | null,
  objectType: PipelineObjectType,
) {
  const [items, setItems] = useState<Pipeline[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!portalId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(`/api/pipelines/${portalId}/${objectType}`, {
      credentials: "include",
    })
      .then(async (res) => {
        const body = (await res.json()) as ApiResponse & { error?: string };
        if (!res.ok) throw new Error(body.error ?? `HTTP ${res.status}`);
        return body;
      })
      .then((body) => {
        if (cancelled) return;
        const list = (body.results ?? []).map((p) => ({
          ...p,
          stages: [...(p.stages ?? [])].sort(
            (a, b) => a.displayOrder - b.displayOrder,
          ),
        }));
        setItems(list);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Unknown error");
        setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [portalId, objectType]);

  return { items, loading, error };
}
